import {
  JSX,
  cloneElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  CSSProperties,
  memo,
  Fragment,
  useMemo,
  RefObject,
} from 'react';
import useClickAwayListener from '@/utils/use-click-away-listener';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import styles from './popover.module.css';
import Button from '@ui/buttons/button/button';
import useArrowKeyCycleFocus from '@/utils/use-arrow-key-cycle-focus';
import useTrackBoundingClient from '@/utils/use-track-bounding-client';
import Portal from '@ui/portal/portal';
import randomString from '@/utils/random-string';
import useDimensionsObserver from '@/utils/use-dimensions-observer';
import useTrackResize from '@/utils/use-track-resize';
import { mergeRefs } from '@/utils/merge-refs';
import useTrackScrollDimensions from '@/utils/use-track-scroll-dimensions';
import globalWindowSize from '@/utils/global-window-size';

type HandleOpenProps = {
  keyDirection?: 'ArrowUp' | 'ArrowDown';
};

/* ensure we're only listening when needed */
type RenderBtnProps = {
  open: boolean;
  closing: boolean;
  setOpen: () => void;
  setClose: () => void;
};

type PopoverProps = {
  ref?: React.Ref<HTMLDivElement>;
  popoverContentRef?: React.Ref<HTMLDivElement>;
  onOpen?: () => void;
  onClose?: () => void;
  icon?: string;
  defaultOpen?: boolean;
  /** only pass in open if you want the component to be controlled  */
  open?: boolean;
  recalcutePosition?: number;
  height?: string;
  maxHeight?: string;
  /**
   * note: closeOnTabAway doesn't work as expected with portals, so by default it will be set to false with portals
   * */
  closeOnTabAway?: boolean;
  width?: string;
  renderTriggerButton?: (btnProps: RenderBtnProps) => JSX.Element;
  forcePosition?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
    | 'center-left';
  closeOnClick?: boolean;
  closeOnEscape?: boolean;
  closeOnEnter?: boolean;
  backgroundColor?: string;
  forceTransformIn?: string;
  forceTransformOut?: string;
  containerClassName?: string;
  contentClassName?: string;
  popoverWidth?: string;
  buttonBgColor?: string;
  disabled?: boolean;
  overflow?: string;
  radius?: string;
  border?: CSSProperties['border'];
  inheritRadius?: boolean;
  children?:
    | string
    | JSX.Element
    | JSX.Element[]
    | ((actions: {
        triggerBounds?: DOMRect | null;
        handleClose: () => void;
        handleOpen: () => void;
        open: boolean;
        closing: boolean;
      }) => JSX.Element);
  horizontalOffset?: number;
  verticalOffset?: number;
  keepOpenOnTriggerToggle?: boolean;
  iconColor?: string;
  iconColorLightMode?: string;
  cycleWithArrowKeys?: boolean;
  portalEl?: React.RefObject<HTMLDivElement | null>;
  refocusTriggerOnClose?: boolean;
  boxShadow?: CSSProperties['boxShadow'];
  openWithArrowKeys?: boolean;
  ignoreClasses?: string[];
  'data-component'?: string;
  stopPropagation?: boolean;
  buttonHoverColor?: string;
  forceClosed?: number;
  onTriggerButtonClick?: (e: React.MouseEvent) => void;
  onPointerUpOutside?: (e: MouseEvent) => void;
  onEscape?: () => void;
  ignoreClickAway?: boolean;
  trackScrollElRef?: RefObject<HTMLElement | null>;
  multiClickAway?: boolean;
  trackOnWindowScroll?: boolean;
};

const Popover = memo(
  ({
    ref,
    popoverContentRef,
    recalcutePosition,
    onOpen,
    onClose,
    defaultOpen = false,
    height,
    maxHeight,
    closeOnTabAway: _closeOnTabAway,
    closeOnEscape = true,
    width = '200px',
    renderTriggerButton,
    children,
    forcePosition,
    closeOnClick = false,
    closeOnEnter = false,
    backgroundColor,
    forceTransformIn,
    forceTransformOut,
    containerClassName,
    contentClassName,
    popoverWidth,
    icon,
    buttonBgColor,
    disabled,
    overflow = 'auto',
    radius,
    inheritRadius,
    horizontalOffset = 0,
    verticalOffset = 0,
    keepOpenOnTriggerToggle,
    open: controlledOpen,
    iconColor,
    iconColorLightMode,
    cycleWithArrowKeys,
    portalEl,
    refocusTriggerOnClose,
    boxShadow = '0px 8px 16px rgba(0, 0, 0, 0.16)',
    openWithArrowKeys = true,
    ignoreClasses = [],
    'data-component': dataComponent,
    stopPropagation = false,
    buttonHoverColor = 'var(--gray-500)',
    forceClosed,
    border,
    onTriggerButtonClick,
    ignoreClickAway,
    onPointerUpOutside,
    onEscape,
    trackScrollElRef,
    multiClickAway,
    trackOnWindowScroll = false,
  }: PopoverProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const contentContainerRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const closeTimeout = useRef<NodeJS.Timeout | null>(null);
    const renderTimer = useRef<NodeJS.Timeout | null>(null);
    const openAutoFocusTimer = useRef<NodeJS.Timeout | null>(null);
    const setDisplayBlockTimer = useRef<NodeJS.Timeout | null>(null);

    const [open, setOpen] = useState(defaultOpen);
    const [closing, setClosing] = useState(false);
    const [display, setDisplay] = useState('none');
    const [mountContent, setMountContent] = useState(false);
    const [slideFrom, setSlideFrom] = useState<'top' | 'bottom' | undefined>();
    const [hasOpenedAtLeastOnce, setHasOpenedAtLeastOnce] = useState(false);
    const [triggerBoundsRecalc, setTriggerBoundsRecalc] = useState(0);

    // important to prevent infinite loop calling of onOpen and onClose
    const previousState = useRef<'open' | 'closed'>(open ? 'open' : 'closed');

    const closeOnTabAway = useMemo(() => {
      if (portalEl && _closeOnTabAway === undefined) {
        return false;
      }
      if (_closeOnTabAway === undefined) {
        return true;
      }
      return _closeOnTabAway;
    }, [_closeOnTabAway, portalEl]);

    const resetPosition = useCallback(
      (shouldUseRect?: boolean) => {
        if (contentContainerRef.current) {
          const rect = contentContainerRef.current.getBoundingClientRect();
          const x = portalEl || shouldUseRect ? rect.x || 0 : 0;
          const y = portalEl || shouldUseRect ? rect.y || 0 : 0;
          contentContainerRef.current.style.top = verticalOffset ? `${y - verticalOffset}px` : `${y}px`;
          contentContainerRef.current.style.left = horizontalOffset ? `${x - horizontalOffset}px` : `${x}px`;
          contentContainerRef.current.style.right = '0';
          contentContainerRef.current.style.bottom = '0';
        }
      },
      [horizontalOffset, portalEl, verticalOffset]
    );

    const calculatePosition = useCallback(() => {
      const containerEl = contentContainerRef.current;
      const contentEl = contentRef.current;
      const triggerEl = triggerRef.current;
      if (!containerEl || !contentEl || !triggerEl) {
        return;
      }
      resetPosition();
      const contentRect = contentEl.getBoundingClientRect();
      const triggerRect = triggerEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();
      const windowWidth = globalWindowSize.innerWidth;
      const windowHeight = globalWindowSize.innerHeight;
      if (contentRect) {
        const right = contentRect.right;
        const left = contentRect.left;
        const bottom = contentRect.bottom;
        let movedHorizontal = false;
        if (forcePosition === 'center-left') {
          // move left
          containerEl.style.left = `-${contentRect.width + horizontalOffset + 8}px`; // 8 is just some spacing
          movedHorizontal = true;
          containerEl.style.top = `-${(contentRect.height + verticalOffset) / 2 - triggerRect.height / 2}px`;
        }
        if (
          (forcePosition && ['top-left', 'bottom-left'].includes(forcePosition)) ||
          (!forcePosition && right - contentRect.width / 2 + triggerRect.width / 2 > windowWidth)
        ) {
          // move left
          // + 4 is an arbitrary number that seems to ensure the content is not cut off
          containerEl.style.left = `-${contentRect.width - triggerEl.offsetWidth + horizontalOffset + 4}px`;
          movedHorizontal = true;
        }
        if (forcePosition && ['top-right', 'bottom-right'].includes(forcePosition)) {
          // move right
          containerEl.style.right = `${contentRect.width - containerRect.width + horizontalOffset}px`;
          movedHorizontal = true;
        }
        if (!forcePosition && left - contentRect.width / 2 + triggerRect.width / 2 < 0) {
          // move right
          containerEl.style.left = 0 + `${horizontalOffset ? `-${horizontalOffset}px` : ''}`;
          movedHorizontal = true;
        }
        if (forcePosition && ['top-center', 'bottom-center'].includes(forcePosition)) {
          // move center
          containerEl.style.left = `${(triggerEl.offsetWidth - contentRect.width) / 2 + horizontalOffset}px`;
          movedHorizontal = true;
        }
        if (
          (forcePosition && ['top-left', 'top-center', 'top-right'].includes(forcePosition)) ||
          (!forcePosition && bottom + triggerRect.height > windowHeight)
        ) {
          // move up
          containerEl.style.top = `-${contentRect.height - verticalOffset}px`;
          if (display === 'none' && open) {
            setSlideFrom('top');
          }
        } else {
          if (
            (forcePosition && ['bottom-left', 'bottom-center', 'bottom-right'].includes(forcePosition)) ||
            !forcePosition
          ) {
            containerEl.style.top = `${triggerRect.height + verticalOffset}px`;
            if (display === 'none' && open) {
              setSlideFrom('bottom');
            }
          }
        }
        if (!movedHorizontal) {
          // center it
          const halfTriggerWidth = triggerRect.width / 2;
          containerEl.style.left = `-${contentRect.width / 2 - halfTriggerWidth + horizontalOffset}px`;
        }
      }
    }, [resetPosition, forcePosition, horizontalOffset, verticalOffset, display, open]);

    const triggerDimensions = useDimensionsObserver({
      ref: triggerRef,
    });

    const windowResize = useTrackResize({
      trackWindow: true,
      trackType: 'debounced',
      debounceTime: 100,
    });

    const { top: scrollElTop } = useTrackScrollDimensions({
      containerRef: trackScrollElRef,
      pause: !open,
    });

    const summedTriggerDimensionsForRecalc =
      (triggerDimensions?.bottom || 0) +
      (triggerDimensions?.top || 0) +
      (triggerDimensions?.right || 0) +
      (triggerDimensions?.left || 0) +
      triggerBoundsRecalc;

    const triggerBounds = useTrackBoundingClient({
      ref: triggerRef,
      debounceTime: 0,
      rerunCounter:
        summedTriggerDimensionsForRecalc +
        (recalcutePosition || 0) +
        windowResize.width +
        windowResize.height +
        scrollElTop,
      open,
      trackOnWindowResize: !!open,
      trackOnWindowScroll: trackOnWindowScroll && open,
    });

    useEffect(() => {
      if (portalEl && open) {
        setTriggerBoundsRecalc(Math.random());
      }
    }, [open, portalEl]);

    useEffect(() => {
      calculatePosition();
    }, [open, triggerBounds, calculatePosition]);

    const handleOpen = useCallback(
      (meta?: HandleOpenProps) => {
        previousState.current = 'open';
        setHasOpenedAtLeastOnce(true);
        onOpen?.();
        if (closeTimeout.current) {
          clearTimeout(closeTimeout.current);
        }
        if (renderTimer.current) {
          clearTimeout(renderTimer.current);
        }
        if (setDisplayBlockTimer.current) {
          clearTimeout(setDisplayBlockTimer.current);
        }

        setMountContent(true);
        setClosing(false);
        setOpen(true);
        /* timeout lets us briefly delay setting the display so we still get the open slide transition animations */
        setDisplayBlockTimer.current = setTimeout(() => {
          setDisplay('block');
        }, 0);
        // If opening via arrow keys, we want to immediately focus content:
        // - wait for popover to open so content is mounted and focusable
        // - if up arrow key, focus the last focusable element
        // - if down arrow key, focus the first focusable element
        if (meta?.keyDirection) {
          if (openAutoFocusTimer.current) {
            clearTimeout(openAutoFocusTimer.current);
          }
          openAutoFocusTimer.current = setTimeout(() => {
            const contentEl = contentRef.current;
            if (!contentEl) return;
            const elementTags =
              'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable]';
            const focusableElements = contentEl.querySelectorAll(elementTags);
            if (meta.keyDirection === 'ArrowUp') {
              const lastFocusableElement = (focusableElements?.[focusableElements.length - 1] ||
                null) as HTMLElement | null;
              if (lastFocusableElement) {
                lastFocusableElement.focus();
              }
            } else if (meta.keyDirection === 'ArrowDown') {
              const firstFocusableElement = (focusableElements?.[0] || null) as HTMLElement | null;
              if (firstFocusableElement) {
                firstFocusableElement.focus();
              }
            }
          }, 150);
        }
      },
      [onOpen]
    );

    const handleClose = useCallback(() => {
      previousState.current = 'closed';
      onClose?.();
      setClosing(true);
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
      if (setDisplayBlockTimer.current) {
        clearTimeout(setDisplayBlockTimer.current);
      }
      closeTimeout.current = setTimeout(() => {
        setOpen(false);
        setClosing(false);
      }, 150);
      setDisplay('none');
      if (renderTimer.current) {
        clearTimeout(renderTimer.current);
      }
      renderTimer.current = setTimeout(() => {
        setMountContent(false);
      }, 150);

      const triggerEl = triggerRef?.current;
      if (refocusTriggerOnClose && triggerEl instanceof Element) {
        if (triggerEl instanceof HTMLButtonElement) {
          triggerEl.focus();
        } else {
          const elAsElement = triggerEl as HTMLElement;
          const firstButton = elAsElement.querySelector('button') as HTMLButtonElement | undefined;
          if (firstButton) {
            firstButton.focus();
          }
        }
      }
    }, [refocusTriggerOnClose, onClose]);

    useEffect(() => {
      if (forceClosed) {
        handleClose();
      }
    }, [forceClosed, handleClose]);

    // call handleOpen/handleClose when controlled component based on open prop (controlledOpen)
    useEffect(() => {
      if (controlledOpen !== undefined) {
        if (controlledOpen) {
          if (previousState.current === 'closed') {
            handleOpen();
          }
        } else {
          if (display === 'block' && mountContent && hasOpenedAtLeastOnce && previousState.current === 'open') {
            handleClose();
          }
        }
      }
    }, [controlledOpen, display, handleClose, handleOpen, hasOpenedAtLeastOnce, mountContent]);

    useClickAwayListener({
      ignoreClasses,
      ref: containerRef,
      open,
      onClickAway: (e) => {
        if (multiClickAway) {
          // if portalEl and the click is inside, return early
          if (portalEl?.current) {
            if (portalEl.current.contains(e.target as Node)) {
              if (closeOnClick) {
                handleClose();
              }
              return;
            }
          }
          handleClose();
        }
      },
      onPointerUpOutside: (e) => {
        onPointerUpOutside?.(e);
        if (!open || ignoreClickAway) return;
        if (ignoreClasses.length > 0 && e.target instanceof Element && e.target.closest(ignoreClasses.join(', '))) {
          return;
        }
        // if portalEl and the click is inside, return early
        if (portalEl?.current) {
          if (portalEl.current.contains(e.target as Node)) {
            if (closeOnClick) {
              handleClose();
            }
            return;
          }
        }
        handleClose();
      },
      onEscape: () => {
        onEscape?.();
        if (closeOnEscape) handleClose();
      },
      onTabAway: () => closeOnTabAway && handleClose(),
      /* use onPointerUpInside instead of onPointerDownInside because onPointerDownInside prevents selecting options if clicking while moving mouse */
      onPointerUpInside: (e) => {
        if (ignoreClasses.length > 0 && e.target instanceof Element && e.target.closest(ignoreClasses.join(', '))) {
          return;
        }
        if (closeOnClick) handleClose();
      },
    });

    useArrowKeyCycleFocus({
      /* ensure we're only listening when needed */
      containerRef: cycleWithArrowKeys && open && display !== 'none' ? portalEl || contentRef : null,
      onTab: () => handleClose(),
      onEnter: () => closeOnEnter && handleClose(),
    });

    const handleOpenWithArrowKeys = useCallback(
      (event: React.KeyboardEvent) => {
        if (openWithArrowKeys && !open && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
          event.preventDefault();
          handleOpen({ keyDirection: event.key });
        }
      },
      [handleOpen, open, openWithArrowKeys]
    );

    const clonedTriggerButton = useCallback(() => {
      return renderTriggerButton
        ? cloneElement(renderTriggerButton({ open, setOpen: handleOpen, setClose: handleClose, closing }), {
            ref: triggerRef,
            disabled,
            onClick: (event: React.MouseEvent) => {
              renderTriggerButton({ open, setOpen: handleOpen, setClose: handleClose, closing })?.props?.onClick?.(
                event
              );
              onTriggerButtonClick?.(event);

              // if disabled, or controlledOpen is defined, then do not call open/close based on click
              // when controleldOpen is defined, we use that prop in a useEffect to determine if we call handleOpen/handleClose
              if (disabled || controlledOpen !== undefined) return;
              if (open) {
                if (keepOpenOnTriggerToggle) return;
                if (
                  ignoreClasses.length > 0 &&
                  event.target instanceof Element &&
                  event.target.closest(ignoreClasses.join(', '))
                ) {
                  return;
                }
                handleClose();
              } else {
                handleOpen();
              }
            },
            onKeyDown: (event: React.KeyboardEvent) => {
              renderTriggerButton({ open, setOpen: handleOpen, setClose: handleClose, closing })?.props?.onKeyDown?.(
                event
              );
              handleOpenWithArrowKeys(event);
            },
          })
        : null;
    }, [
      closing,
      controlledOpen,
      disabled,
      handleClose,
      handleOpen,
      handleOpenWithArrowKeys,
      keepOpenOnTriggerToggle,
      open,
      renderTriggerButton,
      ignoreClasses,
      onTriggerButtonClick,
    ]);

    const ChildrenWrapper = useMemo(() => (portalEl ? Portal : Fragment), [portalEl]);

    const childrenProps = useMemo(() => {
      if (portalEl) {
        return {
          ref: portalEl,
          id: `popover-portal-${randomString(7)}`,
          className: `popover-portal ${styles['popover-portal']} ${display === 'block' ? styles['popover-portal-display'] : ''}`,
        };
      }
      return {};
    }, [display, portalEl]);

    return (
      <div
        data-component={dataComponent || 'Popover'}
        ref={(r) => {
          containerRef.current = r;
          if (ref) {
            if (typeof ref === 'function') {
              ref(r);
            } else {
              ref.current = r;
            }
          }
        }}
        className={`${styles['popover-container']} ${containerClassName || ''}`}
      >
        {clonedTriggerButton() || (
          <Button
            disabled={disabled}
            onClick={(e) => {
              if (stopPropagation) {
                e.stopPropagation();
              }
              onTriggerButtonClick?.(e);
              if (controlledOpen !== undefined) return;
              if (open) {
                if (keepOpenOnTriggerToggle) return;
                handleClose();
              } else {
                handleOpen();
              }
            }}
            onKeyDown={handleOpenWithArrowKeys}
            hoverBackgroundColor={buttonHoverColor}
            ref={triggerRef}
            className={`flex-column jc-center ai-center square-30 radius-full padding-0 ${styles.btn}`}
            style={
              {
                ...(buttonBgColor ? { '--popover-button-bg-color': buttonBgColor } : {}),
              } as CSSProperties
            }
          >
            <span className="flex-column jc-center ai-center">
              <Icon
                name={icon || ICONS.THREE_DOTS_HORIZONTAL}
                size={16}
                color={iconColor}
                lightModeColor={iconColorLightMode || 'var(--gray-1000)'}
              />
            </span>
          </Button>
        )}
        <div
          ref={portalEl ? null : contentContainerRef}
          className={styles['popover-content-container']}
          style={{
            width: popoverWidth || triggerRef.current?.offsetWidth,
            ...(portalEl
              ? {
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  height: 0,
                  width: 0,
                  overflow: 'hidden',
                  zIndex: open ? 'auto' : -1,
                }
              : {}),
          }}
        >
          <div
            className={`${styles['popover-content']} ${contentClassName || ''} ${inheritRadius ? styles['inherit-radius'] : ''} ${open ? styles['open'] : ''} ${display === 'block' ? styles['display-block'] : ''}`}
            style={{
              width: popoverWidth || width,
              transform:
                display === 'block'
                  ? forceTransformIn || `translateY(0)`
                  : forceTransformOut || `translateY(${slideFrom === 'top' ? '10px' : '-10px'})`,
              ...(height ? { height } : { height: 'fit-content' }),
              ...(maxHeight ? { maxHeight } : {}),
              ...(backgroundColor ? { backgroundColor } : {}),
              ...(overflow ? { '--popover-overflow': overflow } : {}),
              ...(radius ? { '--popover-radius': radius } : {}),
              ...(border ? { '--popover-border': border } : {}),
              ...(boxShadow ? { '--popover-box-shadow': boxShadow } : {}),
            }}
            ref={mergeRefs(contentRef, popoverContentRef)}
          >
            {mountContent ? (
              <ChildrenWrapper {...childrenProps}>
                {typeof children === 'function'
                  ? children({
                      triggerBounds,
                      handleClose,
                      handleOpen,
                      open,
                      closing,
                    })
                  : children}
              </ChildrenWrapper>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

export default Popover;
